import { Badge, Button, SkeletonLoader, SkeletonSpinnerCentered, Slider } from '@civilgrid/ui/components';
import { buffer as turfBuffer } from '@turf/turf';
import Dinero from 'dinero.js';
import { isEqual, map } from 'lodash';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAsyncFn } from 'react-use';
import { useDeepCompareMemo } from 'use-deep-compare';
import { useSnapshot } from 'valtio';
import { subscribeKey } from 'valtio/utils';
import FeatureFlags, { OrgFeatureFlag } from '~/authorizations/FeatureFlags';
import { getCost, isZeroCostSubscription } from '~/common/billingUtils';
import { createCircleFromPoint } from '~/common/geoUtils';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import {
  type BillingTierCost,
  type CircleObject,
  EditorMode,
  type LineObject,
  type MapObject,
  type Project,
  ProjectGeometryType,
  type Subscription,
} from '~/common/types';
import InvalidGeometryBadge from '~/components2/Badge/InvalidGeometryBadge';
import InfoTooltip from '~/components2/InfoTooltip';
import { getCircleDestCoords } from '~/components2/Map/canvas/circle/common';
import { shouldConvertToMiles } from '~/components2/Map/canvas/line/common';
import { calculateArea, calculateLength } from '~/helpers/unitHelpers';
import { useValidateGeometry } from '~/hooks/useValidateGeometry';
import { PROJECT_AREA_LIMIT } from '~/pages/common/NewProject/common';
import ExceedsLimitWarning from '~/pages/common/ProjectDataPanel/ExceedsLimitWarning';
import rootStore from '~/store';
import ConfirmationDialog from './ConfirmationDialog';
import { zeroCostCopy } from './common';
import Segments from './Segments';
import store, { CHANGE_ORDER_FEE, type Transaction } from './store';

type ValidGeometry = GeoJSON.MultiPolygon | GeoJSON.MultiLineString;
type GeometryCalcs = { value: number; unit: string };

interface Props {
  project: Project;
  refetch: () => void;
}

function setObjectColors(color: string) {
  store.shouldRedraw = true;
  store.objectsAsArray.forEach((o) => {
    if (!store.currentMap.isLoading) {
      store.currentMap.objects[o.id].properties = {
        ...store.currentMap.objects[o.id].properties,
        color,
      };
    }
  });
}

function EditProjectPanelContent({ project, refetch }: Props) {
  const {
    geometryType,
    editorMode,
    objectsAsArray,
    initialGeometry,
    transactions,
    shouldAddChangeOrderFee,
    geometryAsMultiPolygon,
  } = useSnapshot(store) as typeof store;
  const [smallProjectExceedsAcreage, setSmallProjectExceedsAcreage] = useState(false);
  const objectsAsArrayRef = useRef(0);
  const { currentOrganization } = useSnapshot(rootStore) as typeof rootStore;
  const { subscription } = currentOrganization!;
  const [isLoading, setIsLoading] = useState(true);
  const [rawGeometryCost, setRawGeometryCost] = useState<number>(0);
  const [costDifference, setCostDifference] = useState<number>(0);
  const [initialCost, setInitialCost] = useState<number>(0);
  const [initialRadius, setInitialRadius] = useState<GeometryCalcs | null>(null);
  const [initialArea, setInitialArea] = useState<GeometryCalcs | null>(null);
  const [initialLength, setInitialLength] = useState<GeometryCalcs | null>(null);

  const getMaxSegments = () => {
    if (FeatureFlags.hasOrgFeatureFlag(OrgFeatureFlag.ProjectSegmentLimit100)) {
      return 100;
    }

    return 25;
  };

  const maxSegments = getMaxSegments();

  const validateResult = useValidateGeometry({
    geometry: geometryAsMultiPolygon,
  });

  useEffect(() => {
    const isValid = validateResult.data?.isValid;
    if (isValid === false) {
      setObjectColors('red');
    } else if (isValid === true) {
      setObjectColors('orange');
    }
  }, [validateResult.data?.isValid]);

  const getInitialCost = (transactions: Transaction[]) => {
    const initialCost = transactions.reduceRight(
      (total, { amount }, i) => {
        const t = transactions[i];
        if (t.type === 'credit' || t.type === 'discount') {
          return total.subtract(amount);
        }

        return total.add(amount);
      },
      Dinero({ amount: 0 }),
    );

    setInitialCost(initialCost.getAmount());
  };

  const getInitialLengthOrArea = async () => {
    setIsLoading(true);

    if (geometryType === ProjectGeometryType.Linear) {
      let l = calculateLength({
        unitSystem: store.currentUnitSystem,
        unitType: 'feet',
        geometry: initialGeometry as GeoJSON.MultiLineString,
      });

      if (shouldConvertToMiles({ store, line: l })) {
        l = calculateLength({
          unitSystem: store.currentUnitSystem,
          unitType: 'miles',
          geometry: initialGeometry as GeoJSON.MultiLineString,
        });
      }

      setInitialLength(l);

      const bufferedPolygon = turfBuffer(
        initialGeometry as GeoJSON.MultiLineString,
        (objectsAsArray[0] as LineObject).properties.buffer,
        {
          units: 'feet',
        },
      )!.geometry;

      const a = calculateArea({
        unitSystem: store.currentUnitSystem,
        unitType: 'acres',
        geometry: bufferedPolygon as GeoJSON.MultiPolygon,
      });

      setInitialArea(a);
    } else if (geometryType === ProjectGeometryType.Circle) {
      const l = calculateLength({
        unitSystem: store.currentUnitSystem,
        unitType: 'miles',
        geometry: initialGeometry as GeoJSON.MultiLineString,
      });

      const circleMultiPolygon: GeoJSON.MultiPolygon = {
        type: 'MultiPolygon',
        coordinates: map(objectsAsArray, (o) => {
          const c = o as CircleObject;
          return createCircleFromPoint({
            center: c.geometry,
            radius: c.properties.radius,
          }).coordinates;
        }),
      };

      const a = calculateArea({
        unitSystem: store.currentUnitSystem,
        unitType: 'acres',
        geometry: circleMultiPolygon,
      });

      setInitialRadius(l);
      setInitialArea(a);
    } else {
      const a = calculateArea({
        unitSystem: store.currentUnitSystem,
        unitType: 'acres',
        geometry: initialGeometry as GeoJSON.MultiPolygon,
      });

      setInitialArea(a);
    }

    const rawCost = await getCost({
      projectType: geometryType!,
      geometry: initialGeometry!,
      subscription,
    });

    setRawGeometryCost(rawCost[project.billingTier].totalCost);

    setIsLoading(false);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    getInitialCost(transactions);
  }, [transactions]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    getInitialLengthOrArea();
  }, [initialGeometry]);

  const [projectCost, refreshProjectCost] = useAsyncFn(
    async (projectType: ProjectGeometryType, geometry: ValidGeometry) => {
      return await getCost({
        projectType,
        geometry,
        subscription,
      });
    },
  );

  useEffect(() => {
    const unsubscribe = subscribeKey(store, 'projectArea', () => {
      if (store.projectArea > store.projectAreaLimit) {
        setSmallProjectExceedsAcreage(true);
      } else {
        setSmallProjectExceedsAcreage(false);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    objectsAsArrayRef.current = objectsAsArray.length;
  }, [objectsAsArray]);

  const geomAsGeoJSON = useDeepCompareMemo<GeoJSON.MultiLineString | GeoJSON.MultiPolygon>(() => {
    if (geometryType === ProjectGeometryType.Linear) {
      return {
        type: 'MultiLineString',
        coordinates: map(objectsAsArray, (o) => (o.geometry as GeoJSON.LineString).coordinates),
      };
    } else if (geometryType === ProjectGeometryType.Circle) {
      return {
        type: 'MultiLineString',
        properties: { segmentName: objectsAsArray.map((o) => o.title) },
        coordinates: map(objectsAsArray, (o) => {
          const c = o as CircleObject;
          const radius = c.properties.radius; // pass in as feet, fn handles conversion
          const coords = c.geometry.coordinates;
          const destCoords = getCircleDestCoords({
            lngLat: { lng: coords[0], lat: coords[1] },
            radius,
          }).geometry.coordinates;

          const center = c.geometry.coordinates;
          return [center, destCoords];
        }),
      };
    } else {
      return {
        type: 'MultiPolygon',
        coordinates: map(objectsAsArray, (o) => (o.geometry as GeoJSON.Polygon).coordinates),
      };
    }
  }, [geometryType, objectsAsArray]);

  const getCostDifference = async () => {
    let initialUnits: number;
    let currentUnits: number;
    let subscriptionUnits: number;
    const unitSystem = 'metric';
    let urbanOrRural: 'urban' | 'rural';
    if (projectCost.value?.project.isUrban) {
      urbanOrRural = 'urban';
    } else {
      urbanOrRural = 'rural';
    }

    let billingTierConfig: BillingTierCost;
    if (project.billingTier === 'draft') {
      //@ts-expect-error
      billingTierConfig = {
        polygon: { urban: { unit: 1, cost: 0 }, rural: { unit: 1, cost: 0 } },
        linear: { urban: { unit: 1, cost: 0 }, rural: { unit: 1, cost: 0 } },
        circle: { urban: { unit: 1, cost: 0 }, rural: { unit: 1, cost: 0 } },
      };
    } else {
      billingTierConfig = subscription.projectCost[project.billingTier];
    }

    if (geometryType === ProjectGeometryType.Linear) {
      subscriptionUnits = billingTierConfig.linear[urbanOrRural].unit;

      initialUnits = calculateLength({
        unitSystem,
        unitType: 'miles',
        geometry: initialGeometry as GeoJSON.MultiLineString,
      }).value;

      currentUnits = calculateLength({
        unitSystem,
        unitType: 'miles',
        geometry: geomAsGeoJSON as GeoJSON.MultiLineString,
      }).value;
    } else if (geometryType === ProjectGeometryType.Circle) {
      subscriptionUnits = billingTierConfig.circle[urbanOrRural].unit;

      initialUnits = calculateLength({
        unitSystem,
        unitType: 'miles',
        geometry: initialGeometry as GeoJSON.MultiLineString,
      }).value;

      const circleMultiLine: GeoJSON.MultiLineString = {
        type: 'MultiLineString',
        coordinates: map(objectsAsArray as CircleObject[], (o) => {
          const center = o.geometry.coordinates;
          const destCoords = getCircleDestCoords({
            lngLat: { lng: center[0], lat: center[1] },
            radius: o.properties.radius,
          }).geometry.coordinates;
          return [center, destCoords];
        }),
      };

      currentUnits = calculateLength({
        unitSystem,
        unitType: 'miles',
        geometry: circleMultiLine,
      }).value;
    } else {
      subscriptionUnits = billingTierConfig.polygon[urbanOrRural].unit;

      initialUnits = calculateArea({
        unitSystem,
        unitType: 'acres',
        geometry: initialGeometry as GeoJSON.MultiPolygon,
      }).value;

      currentUnits = calculateArea({
        unitSystem,
        unitType: 'acres',
        geometry: geomAsGeoJSON as GeoJSON.MultiPolygon,
      }).value;
    }

    const threshold = 0.1 * subscriptionUnits;

    const diff = currentUnits - initialUnits;

    // See README for cost methodology
    if (
      currentUnits > subscriptionUnits &&
      diff > threshold &&
      projectCost.value?.[project.billingTier].totalCost &&
      projectCost.value?.[project.billingTier].totalCost >= rawGeometryCost
    ) {
      const newCost = projectCost.value?.[project.billingTier].totalCost - rawGeometryCost;
      setCostDifference(newCost);
    } else {
      setCostDifference(0);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    refreshProjectCost(geometryType!, geomAsGeoJSON);
  }, [geomAsGeoJSON]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    getCostDifference();
  }, [projectCost.value]);

  let area = { unit: 'ac', value: 0 };
  let length = { unit: 'ft', value: 0 };

  if (geometryType === ProjectGeometryType.Linear && objectsAsArray.length > 0) {
    const multiLine = geomAsGeoJSON as GeoJSON.MultiLineString;

    let bufferedPolygon = turfBuffer(multiLine, (objectsAsArray[0] as LineObject).properties.buffer, {
      units: 'feet',
    })!.geometry;

    if (bufferedPolygon.type === 'Polygon') {
      bufferedPolygon = {
        type: 'MultiPolygon',
        coordinates: [bufferedPolygon.coordinates],
      };
    }

    area = calculateArea({
      unitSystem: store.currentUnitSystem,
      geometry: bufferedPolygon,
    });

    let l = calculateLength({
      unitSystem: store.currentUnitSystem,
      geometry: multiLine,
    });

    // if current units are 'feet' and distance is over a half mile, show distance as miles.
    if (shouldConvertToMiles({ store, line: l })) {
      l = calculateLength({
        unitSystem: store.currentUnitSystem,
        unitType: 'miles',
        geometry: multiLine,
      });
    }

    length = l;
  } else if (geometryType === ProjectGeometryType.Circle) {
    const mp: GeoJSON.MultiPolygon = {
      type: 'MultiPolygon',
      coordinates: map(
        objectsAsArray as CircleObject[],
        (o) =>
          createCircleFromPoint({
            center: o.geometry,
            radius: o.properties.radius,
          }).coordinates,
      ),
    };

    area = calculateArea({
      unitSystem: store.currentUnitSystem,
      geometry: mp,
    });
  } else if (geometryType === ProjectGeometryType.Polygon) {
    const multiPoly = geomAsGeoJSON as GeoJSON.MultiPolygon;

    area = calculateArea({
      unitSystem: store.currentUnitSystem,
      geometry: multiPoly,
    });
  }

  if (isLoading) {
    return (
      <div className="absolute right-0 z-10 m-2 flex max-h-[90vh] flex-row overflow-y-scroll">
        <div className="flex h-auto w-[300px] flex-col rounded-sm bg-white p-4">
          <div className="my-2">
            <div className="select-none text-lg text-secondary">Edit Project Shape</div>
          </div>

          <SkeletonSpinnerCentered />
        </div>
      </div>
    );
  }

  return (
    <div className="absolute right-0 z-10 m-2 flex max-h-[90vh] flex-row overflow-y-scroll">
      <Segments />

      <div className="flex h-auto w-[300px] flex-col rounded-sm bg-white p-4">
        <div className="my-2">
          <div className="select-none text-lg text-secondary">Edit Project Shape</div>
        </div>

        <div className="flex items-center justify-start mb-2">
          <InvalidGeometryBadge query={validateResult} />
        </div>

        <BufferSlider />

        <RadiusSlider />

        <CostInfo
          label="Current Project Cost"
          cost={initialCost}
          subscription={subscription}
          tooltip={
            <div className="text-sm">
              <div className="text-base">Project Cost History</div>
              <div className="my-1">
                This shows the price that has already been incurred for this project, starting from project creation and
                any edits thereafter.
              </div>
              <Table headings={['Description', 'Cost']}>
                {transactions.map((t) => {
                  return (
                    <tr key={t.id}>
                      <td className="px-2 text-left">{t.description}</td>
                      <td className="px-2 text-left">{Dinero({ amount: t.amount.getAmount() }).toFormat()}</td>
                    </tr>
                  );
                })}
              </Table>
            </div>
          }
        />

        <UnitInformation label="Current Project Area" info={initialArea} tooltip={'Current project area'} />

        <UnitInformation
          label="Current Project Radius"
          info={geometryType === ProjectGeometryType.Circle ? initialRadius : null}
          tooltip={'Current project radius'}
        />

        <UnitInformation label="Current Project Length" info={initialLength} tooltip={'Current project Length'} />

        <br />

        {/* Project Area and Length Display */}
        <UnitInformation label="New Project Area" info={area} />

        <UnitInformation
          label="New Project Length"
          info={geometryType === ProjectGeometryType.Linear ? length : null}
        />

        <br />

        {project.billingTier === 'draft' ? (
          <>
            <div className="my-2 text-xs text-secondary">
              Note: All edits are free in draft mode. This quote is for your full project cost upon activation.
            </div>
            <CostInfo
              label="Project Cost"
              cost={projectCost.value ? projectCost.value.project.totalCost : 0}
              isLoading={projectCost.loading}
              subscription={subscription}
              tooltip={
                <div className="text-sm">
                  <div className="text-base">Cost Breakdown</div>

                  <Table headings={['Cost', 'Unit']}>
                    {projectCost.value?.project.totalExplanation.map((d, i) => {
                      return (
                        <tr
                          // biome-ignore lint/suspicious/noArrayIndexKey: legacy
                          key={`cost-breakdown-${i}`}
                        >
                          <td className="px-2 text-right">{d.dimension}</td>
                          <td className="px-2 text-right">{d.unitCost}</td>
                          <td className="px-2 text-right">{d.quantity}</td>
                          <td className="px-2 text-right">{d.total}</td>
                        </tr>
                      );
                    })}

                    <tr>
                      <td></td>
                      <td></td>
                      <td className="px-2 text-left">Total</td>
                      <td className="px-2 text-right font-semibold">
                        {Dinero({
                          amount: projectCost.value?.project.totalCost || 0,
                        }).toFormat()}
                      </td>
                    </tr>
                  </Table>
                </div>
              }
            />
          </>
        ) : (
          <>
            <CostInfo
              label="Current Project Cost"
              cost={initialCost}
              subscription={subscription}
              tooltip={
                <div className="text-sm">
                  <div className="text-base">Project Cost History</div>
                  <div className="my-1">
                    This shows the price that has already been incurred for this project, starting from project creation
                    and any edits thereafter.
                  </div>
                  <Table headings={['Description', 'Cost']}>
                    {transactions.map((t, i) => {
                      return (
                        <tr
                          // biome-ignore lint/suspicious/noArrayIndexKey: legacy
                          key={`previous-transactions-${i}`}
                        >
                          <td className="px-2 text-left">{t.description}</td>
                          <td className="px-2 text-left">
                            {Dinero({
                              amount: t.amount.getAmount(),
                            }).toFormat()}
                          </td>
                        </tr>
                      );
                    })}
                  </Table>
                </div>
              }
            />
            <CostInfo
              label="Cost of Project Edit"
              cost={costDifference}
              subscription={subscription}
              isLoading={projectCost.loading}
              tooltip={
                <div className="text-sm">
                  <div className="text-base">Cost Breakdown</div>
                  <div className="my-1">This is the cost that will be incurred for this edit.</div>

                  {costDifference === 0 ? (
                    <div>{zeroCostCopy}</div>
                  ) : (
                    <Table headings={['Cost', 'Unit']}>
                      {projectCost.value?.[project.billingTier].totalExplanation.map((d, i) => {
                        return (
                          <tr
                            // biome-ignore lint/suspicious/noArrayIndexKey: legacy
                            key={`cost-breakdown-${i}`}
                          >
                            <td className="px-2 text-right">{d.dimension}</td>
                            <td className="px-2 text-right">{d.unitCost}</td>
                            <td className="px-2 text-right">{d.quantity}</td>
                            <td className="px-2 text-right">{d.total}</td>
                          </tr>
                        );
                      })}

                      <tr>
                        <td></td>
                        <td></td>
                        <td className="px-2 text-left">Total</td>
                        <td className="px-2 text-right font-semibold">
                          {Dinero({
                            amount: projectCost.value?.[project.billingTier].totalCost || 0,
                          }).toFormat()}
                        </td>
                      </tr>
                    </Table>
                  )}
                </div>
              }
            />
            {shouldAddChangeOrderFee && (
              <CostInfo
                label="Change Order Fee"
                cost={CHANGE_ORDER_FEE.getAmount()}
                subscription={subscription}
                isLoading={projectCost.loading}
                tooltip={
                  <div className="text-sm">
                    Since the project has existing data requests, a {CHANGE_ORDER_FEE.toFormat()} Change Order Fee will
                    be added.
                  </div>
                }
              />
            )}
          </>
        )}

        <br />
        {area && area.value > PROJECT_AREA_LIMIT && (
          <Badge color="red" className="my-2 px-4 py-2 text-base">
            Maximum project size is {PROJECT_AREA_LIMIT.toLocaleString()} acres.
          </Badge>
        )}

        {/* Displays a warning for small 3-acre projects if they exceed limit */}
        <ExceedsLimitWarning store={store} exceeds={smallProjectExceedsAcreage} />

        <div className="flex space-x-2 self-baseline">
          <Button
            type="button"
            variant="secondary"
            data-testid="project-edit-geometry-discard-changes"
            onClick={() => {
              if (store.project.isLoading === false) {
                capturePosthogEvent(POSTHOG_EVENTS.projectBoundaryDiscarded, {
                  project_id: store.project.id,
                  organization_id: store.project.organization || undefined,
                });
              }

              store.reset();
            }}
          >
            Discard Changes
          </Button>
          <ConfirmationDialog
            project={project}
            refetch={refetch}
            geometry={geomAsGeoJSON}
            costDifference={costDifference}
            disabledReason={!validateResult.data?.isValid ? 'Invalid project geometry' : undefined}
            isDisabled={
              !validateResult.data?.isValid ||
              objectsAsArray.length === 0 ||
              objectsAsArray.length >= maxSegments ||
              (area && area.value > PROJECT_AREA_LIMIT) ||
              smallProjectExceedsAcreage ||
              (isEqual(initialGeometry, geomAsGeoJSON) && initialArea?.value === area.value)
            }
          />
        </div>

        {editorMode.startsWith('Draw') && (
          <div className="mt-4 w-full space-y-2 self-end rounded-full border p-2 px-4 text-center">
            <div>Click anywhere to begin</div>
            {(editorMode === EditorMode.DrawLine || editorMode === EditorMode.DrawPolygon) && (
              <div>
                Press{' '}
                <code className="rounded-lg border border-gray-200 bg-gray-100 px-2 py-1.5 text-2xs font-semibold text-gray-800 dark:border-gray-500 dark:bg-gray-600 dark:text-gray-100">
                  Enter
                </code>{' '}
                or double click to end
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const CostInfo = ({
  cost,
  label,
  isLoading,
  tooltip,
  subscription,
}: {
  cost: number;
  label: string;
  isLoading?: boolean;
  tooltip?: React.ReactNode;
  subscription: Subscription;
}) => {
  const isZeroCostOrg = useMemo(() => {
    return isZeroCostSubscription(subscription);
  }, [subscription]);

  if (isZeroCostOrg === true) {
    return null;
  }

  if (isLoading === true) {
    return (
      <div className="flex">
        <div>{label}: </div>
        <InfoTooltip text={tooltip} label={`About ${label}`} />
        <div className="inline-flex items-center align-middle ml-1">
          <SkeletonLoader width={50} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex">
      <div>{label}: </div>
      <InfoTooltip text={tooltip} label={`About ${label}`} />
      <div className="ml-1 font-bold">{Dinero({ amount: cost }).toFormat()}</div>
    </div>
  );
};

const UnitInformation = ({
  label,
  info,
  tooltip,
}: {
  label: string;
  info: GeometryCalcs | null;
  tooltip?: React.ReactNode;
}) => {
  if (!info) {
    return null;
  }

  return (
    <div className="flex">
      <div>{label}: </div>
      {tooltip && <InfoTooltip text={tooltip} label={`About ${label}`} />}
      <div className="ml-1 font-bold">
        {info.value.toLocaleString()} {info.unit}
      </div>
    </div>
  );
};

const Table = ({ headings, children }: { headings: string[]; children?: React.ReactNode }) => {
  return (
    <table className="border-spacing-10">
      <thead>
        <tr>
          {headings.map((h, i) => {
            return (
              <th
                // biome-ignore lint/suspicious/noArrayIndexKey: legacy
                key={`table-heading-${h}-${i}`}
              >
                {h}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
};

export function BufferSlider() {
  const { objectsAsArray, geometryType } = useSnapshot(store);
  if (!objectsAsArray.length) return null;
  if (geometryType !== ProjectGeometryType.Linear) return null;

  return (
    <Slider
      className="my-6"
      label="Buffer"
      labelClassName="text-sm font-normal"
      tooltipText="The buffer width is added to each side of the line."
      min={10}
      max={150}
      step={1}
      value={(objectsAsArray[0] as LineObject).properties.buffer || store.lineBuffer}
      formatValue={(value) => `${value} ft`}
      onValueChange={(value) => {
        // update buffer for existing objects
        store.objectsAsArray.forEach((o: MapObject) => {
          (o as LineObject).properties.buffer = value;
        });
        store.shouldRedraw = true;

        // set buffer for objects that will be created
        store.updateLineBuffer(value);
      }}
      data-testid="edit-project-buffer-slider"
    />
  );
}

export function RadiusSlider() {
  const { objectsAsArray, geometryType } = useSnapshot(store);
  if (objectsAsArray.length <= 0) return null;
  if (geometryType !== ProjectGeometryType.Circle) return null;

  const circle = objectsAsArray[0] as CircleObject;

  const r = circle.properties.radius / 5_280;
  const value = Number(r.toFixed(2));

  return (
    <Slider
      className="my-6"
      label="Radius"
      labelClassName="text-sm font-normal"
      min={0.1}
      max={1}
      step={0.05}
      value={value}
      formatValue={(value) => `${value} mi`}
      onValueChange={(value) => {
        (store.objectsAsArray as CircleObject[]).forEach((o) => {
          o.properties.radius = value * 5_280;
        });

        store.shouldRedraw = true;
      }}
      data-testid="edit-project-radius-slider"
    />
  );
}

export function EditProjectPanel({ ...props }: Props) {
  const { currentOrganization } = useSnapshot(rootStore) as typeof rootStore;
  const subscription = currentOrganization?.subscription;

  if (!subscription) return null;

  return <EditProjectPanelContent {...props} />;
}
